import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Select, MenuItem,
  FormControl, InputLabel, Avatar, TextField, InputAdornment,
} from '@mui/material';
import { Nfc as NfcIcon, Search as SearchIcon } from '@mui/icons-material';
import corporateService, { CorporateBracelet, CorporateClient } from '../../services/corporate.service';

const statusColor = (s?: string): 'success' | 'warning' | 'error' | 'default' => {
  if (!s) return 'default';
  if (s === 'ACTIVE') return 'success';
  if (s === 'BLOCKED') return 'error';
  if (s === 'ASSIGNED') return 'warning';
  return 'default';
};

const BraceletsPage: React.FC = () => {
  const [bracelets, setBracelets] = useState<CorporateBracelet[]>([]);
  const [companies, setCompanies] = useState<CorporateClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCompany, setFilterCompany] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, c] = await Promise.all([
        corporateService.getBracelets(filterCompany ? { company_id: filterCompany as number } : {}),
        corporateService.getAll(),
      ]);
      setBracelets(b);
      setCompanies(c);
    } catch {
      setError('Erro ao carregar bracelets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterCompany]);

  const filtered = bracelets.filter(b => {
    if (filterStatus === 'with_bracelet' && !b.nfc_uid) return false;
    if (filterStatus === 'without_bracelet' && b.nfc_uid) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!b.employee_nome.toLowerCase().includes(q) &&
          !b.company_nome.toLowerCase().includes(q) &&
          !(b.nfc_uid || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const withBracelet = bracelets.filter(b => !!b.nfc_uid).length;
  const withoutBracelet = bracelets.length - withBracelet;
  const active = bracelets.filter(b => b.bracelet_status === 'ACTIVE').length;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Avatar sx={{ bgcolor: '#6233A0' }}><NfcIcon /></Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>Bracelets NFC</Typography>
          <Typography variant="body2" color="text.secondary">
            Estado de todos os bracelets corporativos
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Total funcionários', value: bracelets.length, color: '#6233A0' },
          { label: 'Com bracelet',       value: withBracelet,     color: '#10B981' },
          { label: 'Sem bracelet',       value: withoutBracelet,  color: '#F59E0B' },
          { label: 'Activos',            value: active,           color: '#0D7F9F' },
        ].map(s => (
          <Paper key={s.label} sx={{ p: 2, minWidth: 140, flex: 1 }}>
            <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Filtros */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Pesquisar funcionário, empresa ou UID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flex: 2, minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Empresa</InputLabel>
          <Select value={filterCompany} label="Empresa" onChange={e => setFilterCompany(e.target.value as number | '')}>
            <MenuItem value="">Todas</MenuItem>
            {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Bracelet</InputLabel>
          <Select value={filterStatus} label="Bracelet" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="with_bracelet">Com bracelet</MenuItem>
            <MenuItem value="without_bracelet">Sem bracelet</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><b>Funcionário</b></TableCell>
                <TableCell><b>Empresa</b></TableCell>
                <TableCell><b>Departamento</b></TableCell>
                <TableCell><b>UID Bracelet</b></TableCell>
                <TableCell align="center"><b>Estado</b></TableCell>
                <TableCell align="right"><b>Saldo Bonus (ECV)</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum resultado
                  </TableCell>
                </TableRow>
              ) : filtered.map(b => (
                <TableRow key={`${b.company_id}-${b.employee_id}`} hover>
                  <TableCell>{b.employee_nome}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{b.company_nome}</Typography>
                  </TableCell>
                  <TableCell>{b.departamento || '—'}</TableCell>
                  <TableCell>
                    {b.nfc_uid ? (
                      <Typography variant="caption" fontFamily="monospace">{b.nfc_uid}</Typography>
                    ) : (
                      <Chip label="Sem bracelet" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {b.nfc_uid ? (
                      <Chip
                        label={b.bracelet_status || 'ASSIGNED'}
                        size="small"
                        color={statusColor(b.bracelet_status)}
                      />
                    ) : (
                      <Chip label="—" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {Number(b.saldo_bonus) > 0
                      ? Number(b.saldo_bonus).toLocaleString('pt-CV')
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BraceletsPage;
